import api from './api';

const B = '/api/communications';

// Communications Hub (Phase 1A — portal messaging).
export const commsAPI = {
  list: (params) => api.get(`${B}/conversations`, { params }),
  get: (id, markRead = true) => api.get(`${B}/conversations/${id}`, { params: { mark_read: markRead } }),
  create: (data) => api.post(`${B}/conversations`, data),
  reply: (id, body, extra = {}) => api.post(`${B}/conversations/${id}/messages`, { body, ...extra }),
  patch: (id, data) => api.patch(`${B}/conversations/${id}`, data),
  assign: (id, data) => api.post(`${B}/conversations/${id}/assign`, data),
  // action: done | reopen | read | unread
  action: (id, action) => api.post(`${B}/conversations/${id}/${action}`),
  bulk: (data) => api.post(`${B}/conversations/bulk`, data),
  addNote: (id, data) => api.post(`${B}/conversations/${id}/notes`, data),
  deleteNote: (noteId) => api.delete(`${B}/notes/${noteId}`),
  unreadCount: () => api.get(`${B}/unread-count`),
  staff: () => api.get(`${B}/staff`),
};

export const STATUS_META = {
  open: { label: 'Open', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  waiting_client: { label: 'Waiting on client', className: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200' },
  done: { label: 'Done', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
};

export const initials = (name) => (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || '?';

export const errMsg = (err, fallback = 'Something went wrong') => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return fallback;
};

export const notifyCommsChanged = () => window.dispatchEvent(new Event('comms:changed'));

export const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (now - d < 6 * 864e5) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

export const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : '');

// ----- Phase 1B: attachments, drafts, client overview -----
export const commsFilesAPI = {
  upload: (files, conversationId) => {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (conversationId) fd.append('conversation_id', conversationId);
    return api.post(`${B}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  blob: (id, inline = false) => api.get(`${B}/attachments/${id}/download`, { params: { inline }, responseType: 'blob' }),
  remove: (id) => api.delete(`${B}/attachments/${id}`),
  saveToDocuments: (id, visibility) => api.post(`${B}/attachments/${id}/save-to-documents`, { visibility }),
  getDraft: (convId) => api.get(`${B}/conversations/${convId}/draft`),
  saveDraft: (convId, body) => api.put(`${B}/conversations/${convId}/draft`, { body, body_format: 'html' }),
  drafts: () => api.get(`${B}/drafts`),
  timeline: (clientId, params) => api.get(`${B}/clients/${clientId}/timeline`, { params }),
  addFileNote: (clientId, body) => api.post(`${B}/clients/${clientId}/file-notes`, { body, body_format: 'html' }),
};

export const notificationsAPI = {
  list: (params) => api.get('/api/notifications', { params }),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  read: (id) => api.post(`/api/notifications/${id}/read`),
  readAll: () => api.post('/api/notifications/read-all'),
  prefs: () => api.get('/api/notifications/preferences'),
  savePrefs: (prefs) => api.put('/api/notifications/preferences', { prefs }),
};

export const fmtBytes = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

// Blob error bodies (axios responseType=blob) → readable message.
export const blobErrMsg = async (err, fallback) => {
  try { const t = await err.response.data.text(); return JSON.parse(t).detail || fallback; } catch { return fallback; }
};

// ----- Phase 2: mailboxes + email -----
export const mailboxAPI = {
  providers: () => api.get(`${B}/accounts/providers`),
  list: () => api.get(`${B}/accounts`),
  usable: () => api.get(`${B}/accounts/usable`),
  connect: (provider, data) => api.post(`${B}/accounts/${provider}/connect`, data),
  patch: (id, data) => api.patch(`${B}/accounts/${id}`, data),
  setDefault: (id) => api.post(`${B}/accounts/${id}/default`),
  reauthorise: (id) => api.post(`${B}/accounts/${id}/reauthorise`),
  syncNow: (id) => api.post(`${B}/accounts/${id}/sync-now`),
  syncLog: (id) => api.get(`${B}/accounts/${id}/sync-log`),
  disconnect: (id) => api.delete(`${B}/accounts/${id}`),
  signature: () => api.get(`${B}/settings/email-signature`),
  saveSignature: (html) => api.put(`${B}/settings/email-signature`, { signature_html: html }),
};

export const emailAPI = {
  sendNew: (data) => api.post(`${B}/email`, data),
  send: (convId, data) => api.post(`${B}/conversations/${convId}/email`, data),
  saveDraft: (convId, data) => api.put(`${B}/conversations/${convId}/email-draft`, data),
  link: (convId, clientId, remember) => api.post(`${B}/conversations/${convId}/link`, { client_id: clientId, remember }),
  unlink: (convId) => api.post(`${B}/conversations/${convId}/unlink`),
  notClient: (convId) => api.post(`${B}/conversations/${convId}/not-client`),
  clientSearch: (q) => api.get(`${B}/client-search`, { params: { q } }),
};

export const splitAddrs = (s) => (s || '').split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
